import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { teacherCockpit } from "@/lib/api";

const ZONE_LABEL = {
  needs_attention: "Needs Attention",
  gone_quiet:      "Gone Quiet",
  struggling:      "Struggling",
  flying:          "Flying",
  not_started:     "Not started",
};

const ZONE_TEXT = {
  needs_attention: "text-rose-700",
  gone_quiet:      "text-orange-700",
  struggling:      "text-amber-700",
  flying:          "text-emerald-700",
  not_started:     "text-slate-500",
};

function humanizeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

/**
 * Print-friendly one-page class report. Opens in a new tab.
 * Auto-triggers window.print() once rendered. @media print styles
 * remove the action bar.
 */
export default function TeacherClassPrint() {
  const { teacherId } = useRole();
  const { grade, subject } = useParams();
  const subjectDecoded = decodeURIComponent(subject || "");
  const gradeNum = parseInt(grade, 10);

  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const d = await teacherCockpit(teacherId, gradeNum, subjectDecoded);
      setData(d);
      // small delay so the layout paints before print dialog
      setTimeout(() => { try { window.print(); } catch { /* ignore */ } }, 400);
    })();
  }, [teacherId, gradeNum, subjectDecoded]);

  if (!data) {
    return <div className="p-10 text-center text-slate-400">Preparing report…</div>;
  }

  // Flatten all zones into a single learner roster, preserving zone identity.
  const rosterRows = [];
  ["needs_attention", "gone_quiet", "struggling", "flying"].forEach(z => {
    (data.zones[z] || []).forEach(r => rosterRows.push({ ...r, zone: z }));
  });
  (data.not_started || []).forEach(r => rosterRows.push({ ...r, zone: "not_started" }));

  const today = new Date().toLocaleDateString(undefined, {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="bg-white text-slate-900 min-h-screen print-page">
      {/* Print-specific CSS — embedded for portability */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-page { padding: 0 !important; }
          @page { size: A4 portrait; margin: 14mm 12mm; }
        }
        .print-page { padding: 32px 40px; max-width: 900px; margin: 0 auto; }
        .roster-table thead { background: #f1f5f9; }
        .roster-table th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
                           text-align: left; padding: 8px 10px; border-bottom: 1px solid #cbd5e1;
                           color: #475569; font-weight: 700; }
        .roster-table td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px;
                           vertical-align: top; }
      `}</style>

      <div className="no-print flex justify-between items-center mb-6">
        <button
          onClick={() => window.close()}
          data-testid="print-close-btn"
          className="text-sm font-semibold text-slate-600 hover:text-slate-900"
        >
          ← Close
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            data-testid="print-trigger-btn"
            className="text-sm font-semibold bg-slate-900 text-white rounded-lg px-4 py-2 hover:bg-slate-800"
          >
            Print Report
          </button>
          <button
            onClick={() => window.print()}
            data-testid="download-pdf-btn"
            className="text-sm font-semibold bg-sky-600 text-white rounded-lg px-4 py-2 hover:bg-sky-700"
          >
            Download as PDF
          </button>
        </div>
      </div>

      <header className="border-b-2 border-slate-900 pb-4 mb-6" data-testid="print-header">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Class report</div>
        <div className="font-teacher text-3xl font-bold mt-1">
          Grade {grade} · {subjectDecoded}
        </div>
        <div className="text-sm text-slate-600 mt-1">
          Class teacher: <span className="font-semibold">{teacherId}</span> · Date: {today}
        </div>
      </header>

      <div className="grid grid-cols-4 gap-2 mb-6 text-center" data-testid="print-summary-tiles">
        {["needs_attention", "gone_quiet", "struggling", "flying"].map(z => (
          <div key={z} className="border border-slate-200 rounded-lg p-3">
            <div className={`text-2xl font-bold font-teacher ${ZONE_TEXT[z]}`}>
              {data.summary[z]}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-slate-600 mt-0.5">
              {ZONE_LABEL[z]}
            </div>
          </div>
        ))}
      </div>

      {rosterRows.length === 0 ? (
        <div className="text-center text-slate-500 py-10" data-testid="print-empty">
          No learners to report.
        </div>
      ) : (
        <table className="w-full roster-table" data-testid="print-roster">
          <thead>
            <tr>
              <th>Student</th>
              <th>Zone</th>
              <th>Current sub-topic</th>
              <th>Last active</th>
              <th>Needs action?</th>
            </tr>
          </thead>
          <tbody>
            {rosterRows.map(r => (
              <tr key={r.learner_id} data-testid={`print-row-${r.learner_id}`}>
                <td className="font-semibold">{r.name}</td>
                <td className={ZONE_TEXT[r.zone] + " font-semibold"}>{ZONE_LABEL[r.zone]}</td>
                <td>{r.current_sub_topic || "—"}</td>
                <td>{humanizeDate(r.last_active_at)}</td>
                <td className={r.zone === "needs_attention" ? "text-rose-700 font-semibold" : "text-slate-500"}>
                  {r.zone === "needs_attention" ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="border-t-2 border-slate-900 mt-6 pt-3 text-sm text-slate-700" data-testid="print-summary-line">
        <strong>Summary:</strong>{" "}
        {data.summary.needs_attention} needs attention,{" "}
        {data.summary.gone_quiet} gone quiet,{" "}
        {data.summary.struggling} struggling,{" "}
        {data.summary.flying} flying
        {data.summary.not_started > 0 && <>, {data.summary.not_started} not started</>}.
        {data.chapters_complete?.length > 0 && (
          <div className="mt-2">
            <strong>Chapters complete for all students:</strong>{" "}
            {data.chapters_complete.map(c => c.topic).join(", ")}.
          </div>
        )}
      </footer>
    </div>
  );
}
